// LB-18 §8.5 — per-section debounced save queue for the listing builder.
//
// Each section of a builder step gets its own saver. `set(key, value)`
// coalesces edits per key; after `debounceMs` of quiet the saver posts ONLY
// that section's pending keys (`{ id, ...pendingKeys }`, never the whole
// home), so an FAQ edit can no longer rewrite KPIs, metrics or at-a-glance.
//
// States: idle → saving → saved(at) | error(kind) | conflict
//   error kinds: retryable | validation | locked | hold | permission
// - network / 5xx: retried at 1, 2 and 4 s, then error(retryable); the edit
//   stays queued and is never dropped ("Couldn't save · Retry").
// - Epic D45 (freeze and reload): a 409 version_conflict, a 428
//   version_required or any other sign the listing moved without this tab
//   freezes the LISTING (listing-freeze.js). Every saver of a frozen
//   listing shows `conflict`, keeps its edits in memory and sends nothing;
//   the banner lists the unsaved text and its Reload clears them. There is
//   no per-section Keep mine / Use theirs.
// - 400 terms_locked_live / code 201 (submitted): error(locked), read-only.
// - 423: error(hold). 401/403: error(permission). Other 4xx: validation.
// - A key sent empty ("" / null / []) that comes back non-empty is reported
//   as `uncleared` ("This field can't be emptied yet"), never as "Saved"
//   (OD-LB18-5: 41 update_home setters ignore empty values today).
//
// `flush()` resolves only when nothing is pending or in flight (or the
// section stopped on an error / the listing is frozen). On
// visibilitychange→hidden and `pagehide` the pending default-endpoint
// payloads go out with `fetch(..., { keepalive: true })` — never for a
// frozen listing, nor while this tab still has a write running for it; a
// failed keepalive turns the section into error (re-sent when the page is
// visible again) or freezes the listing (409 / 428). `beforeunload` prompts
// while edits are unsaved (a frozen listing's included).
//
// LB-26 P1-1 / P1-2:
// - Every saver keeps `inFlight` values (the request on the wire); the
//   frozen-listing dialog lists `inFlight ∪ pending`, so a 409 never shows
//   "no unsaved changes" for a value it just refused. A failed send restores
//   its keys into `pending` before anything else runs.
// - `unsavedState.revision` (reactive) is bumped on every change to what a
//   saver holds, so the dialog re-captures while it is open.
// - Baseline (dirty check): a page primes each saver with its own payload
//   builder run on freshly hydrated data (`prime()`); per-key `set()` drops a
//   key that equals the baseline (or the value in flight) — A→B→A sends
//   nothing — and whole-payload `replacePending()` is a gate: the full
//   payload goes only when some key differs. A successful send becomes the
//   new baseline. Equality: `valuesEqual` below.
import Vue from "vue";
import axios from "axios";
import en from "@/locales/en.json";
import { PAPERWORK_KEYS } from "@/services/offering-documents";
import { fieldLabel, optionLabel } from "@/services/listing-terms";
import backend, {
  getHomeVersion,
  hasPendingHomeWrite,
  holdHomeWrite,
  setHomeVersion,
  trackHomeWrite,
  wasHydrated,
} from "@/services/backend";
import {
  anyUnsavedFromSources,
  conflictCurrentFor,
  flushListingInputs,
  freezeHome,
  isHomeArchived,
  isHomeFrozen,
  isListingFrozenError,
  recordConflictCurrent,
  registerPendingProbe,
  unsavedFromSources,
} from "@/services/listing-freeze";

export const SAVE_STATUS = Object.freeze({
  IDLE: "idle",
  SAVING: "saving",
  SAVED: "saved",
  ERROR: "error",
  CONFLICT: "conflict",
});

export const DEFAULT_DEBOUNCE_MS = 800;
export const DEFAULT_RETRY_DELAYS = Object.freeze([1000, 2000, 4000]);
const UPDATE_HOME_URL = "/api/homes/update";

const liveSavers = new Set();
// Disposed savers that still held unsaved edits (an editor unmounted while
// frozen, or its last flush failed): kept, unsent, until Reload so the
// banner still lists them and the leave prompt stays.
const orphanSavers = new Set();
// Savers whose page-exit (keepalive) send failed: re-sent, versioned, when
// the page is visible again.
const retryWhenVisible = new Set();

// Build Listing accordions → the update_home keys each one owns (§8.5).
export const BUILD_LISTING_SECTIONS = Object.freeze({
  description: [
    "investment_overview",
    "primary_investment_objective",
    "property_type",
    "funding_purpose",
  ],
  details: ["additional_data"],
  // supporting_documents are not here: each document upload / removal is
  // its own side route (upload-document / delete-document), saved at once.
  uploads: ["contract", "overview_video", "webinar", "supporting_videos"],
  kpis: ["kpis"],
  // LB-9: risk_factors moved to the Paperwork section.
  structure: ["investment_structure", "description_of_securities"],
  top_metrics: ["top_metrics"],
  at_a_glance: ["at_a_glance"],
  tab_order: ["tab_order"],
  faqs: ["faqs"],
  // LB-9 Paperwork (filings, statement-tier inputs, risks). Queued from
  // PaperworkSection.dirtyPayload() (only what the issuer changed), not
  // from the page's full payload; documents are side routes.
  paperwork: PAPERWORK_KEYS.slice(),
});

function isEmptyValue(value) {
  return (
    value === "" ||
    value === null ||
    (Array.isArray(value) && value.length === 0)
  );
}

// The backend's hydration rule asks whether this tab still has unsaved
// edits for a listing (they would have been typed over an older version).
registerPendingProbe((homeId) =>
  allSavers().some(
    (saver) => saver.versioned && String(saver.homeId) === String(homeId) && saver.hasPending()
  )
);

function allSavers() {
  return [...Array.from(liveSavers), ...Array.from(orphanSavers)];
}

// LB-18 §11 events: ids, section names and kinds only — never values.
export function trackSaveEvent(name, params) {
  try {
    if (typeof window !== "undefined" && typeof window.gtag === "function") {
      window.gtag("event", name, params || {});
    }
  } catch (_) {
    // Analytics must never affect saving.
  }
}

// LB-26 P1-2 equality rules for the dirty check:
// - null, undefined and "" are equal to each other only (never to 0 / false);
// - strings compare as strings ("02134" !== "2134"; ZIP / CIK keep zeros);
// - numbers compare numerically only when both sides are numbers
//   (1 !== "1": the page must hydrate the type its inputs emit);
// - arrays are order-sensitive (tab order and FAQ order are real changes);
// - objects are deep-equal regardless of key order (a missing key equals
//   null / undefined / "").
function isBlank(value) {
  return value === null || value === undefined || value === "";
}

export function valuesEqual(a, b) {
  if (isBlank(a) || isBlank(b)) return isBlank(a) && isBlank(b);
  if (typeof a === "number" && typeof b === "number") {
    return a === b || (Number.isNaN(a) && Number.isNaN(b));
  }
  if (Array.isArray(a) || Array.isArray(b)) {
    if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
    return a.every((item, index) => valuesEqual(item, b[index]));
  }
  if (typeof a !== typeof b) return false;
  if (typeof a === "object") {
    const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
    return Array.from(keys).every((key) => valuesEqual(a[key], b[key]));
  }
  return a === b;
}

// LB-26 R2-2: a detached copy of plain arrays / objects (anything else —
// a File, a Date — is kept as is). Pages hand the saver live reactive
// arrays (Propose Terms' `money_allocation` rows are mutated in place by
// v-model), so a queued or in-flight value that kept the page's reference
// would silently "become" the newer edit: the edit then compared equal to
// the request on the wire and was never sent, while "Saved" showed.
export function snapshotValue(value) {
  if (Array.isArray(value)) return value.map(snapshotValue);
  if (!value || typeof value !== "object") return value;
  const proto = Object.getPrototypeOf(value);
  if (proto !== Object.prototype && proto !== null) return value;
  const out = {};
  Object.keys(value).forEach((key) => {
    out[key] = snapshotValue(value[key]);
  });
  return out;
}

function cloneValue(value) {
  if (value === undefined) return undefined;
  try {
    return JSON.parse(JSON.stringify(value));
  } catch (_) {
    return value;
  }
}

// Reactive: bumped whenever what a saver holds changes (set / setMany /
// replacePending / prime / send / settle / restore / discard). The
// frozen-listing dialog watches it and re-captures the unsaved list.
export const unsavedState = Vue.observable({ revision: 0 });

export function bumpUnsavedRevision() {
  unsavedState.revision += 1;
}

// HCD-13: one copy table (`lb.save.*` in en.json) for every save status.
const SAVE_COPY = (en && en.lb && en.lb.save) || {};

export function saveCopy(key, params) {
  const template = SAVE_COPY[key] || key;
  return String(template).replace(/\{(\w+)\}/g, (match, name) =>
    params && params[name] !== undefined ? String(params[name]) : match
  );
}

// HCD-13: a raw server string (a field key, an enum, "override_reason",
// "undefined") never reaches the user; plain-language messages pass.
export function plainSaveMessage(message, fallbackKey = "error_generic") {
  const text = String(message == null ? "" : message).trim();
  if (!text) return saveCopy(fallbackKey);
  // "security_type: Not allowed" -> "Not allowed".
  const stripped = text.replace(/^[a-z0-9_.[\]]+:\s*/, "");
  if (
    /\bundefined\b|\bnull\b|\[object /.test(stripped) ||
    /\b[a-z0-9]+_[a-z0-9_]+\b/.test(stripped) ||
    /<[^>]+>/.test(stripped)
  ) {
    return saveCopy(fallbackKey);
  }
  return stripped;
}

function defaultSleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

const FROZEN_STATE_MESSAGE = "Changed in another tab or by a teammate";

function isVersionError(error) {
  const response = error && error.response;
  const data = (response && response.data) || {};
  return (
    (error && error.name === "VersionConflictError") ||
    isListingFrozenError(error) ||
    (response && response.status === 409 && data.code === "version_conflict") ||
    (response && response.status === 428) ||
    data.code === "version_required"
  );
}

export function classifySaveError(error) {
  if (isVersionError(error)) {
    return { status: SAVE_STATUS.CONFLICT, frozen: true, message: FROZEN_STATE_MESSAGE };
  }
  const response = error && error.response;
  const data = (response && response.data) || {};
  const message =
    data.message || (error && error.message) || "Couldn't save";
  // No response at all is a network failure (retryable). A response without
  // a status (a server-shaped error) is not retried.
  if (!response) {
    return { status: SAVE_STATUS.ERROR, kind: "retryable", message };
  }
  const code = response.status || 400;
  if (code >= 500) return { status: SAVE_STATUS.ERROR, kind: "retryable", message };
  if (code === 423) return { status: SAVE_STATUS.ERROR, kind: "hold", message };
  if (code === 401 || code === 403) {
    return { status: SAVE_STATUS.ERROR, kind: "permission", message };
  }
  if (
    data.code === "terms_locked_live" ||
    data.code === 201 ||
    data.code === "listing_archived"
  ) {
    return { status: SAVE_STATUS.ERROR, kind: "locked", code: data.code, message };
  }
  return { status: SAVE_STATUS.ERROR, kind: "validation", message };
}

export function savedTimeText(savedAt) {
  return savedAt
    ? saveCopy("saved_at", {
        time: new Date(savedAt).toLocaleTimeString([], {
          hour: "numeric",
          minute: "2-digit",
        }),
      })
    : saveCopy("saved");
}

export function statusText(state) {
  if (!state) return "";
  switch (state.status) {
    case SAVE_STATUS.SAVING:
      return saveCopy("saving");
    case SAVE_STATUS.SAVED:
      if (state.uncleared && state.uncleared.length) {
        return saveCopy("uncleared");
      }
      return savedTimeText(state.savedAt);
    case SAVE_STATUS.CONFLICT:
      return saveCopy("conflict");
    case SAVE_STATUS.ERROR:
      if (state.kind === "retryable") return saveCopy("error_retryable");
      if (state.kind === "locked") {
        if (state.code === "listing_archived") return saveCopy("archived");
        if (state.code === 201) return saveCopy("submitted");
        return saveCopy("locked");
      }
      if (state.kind === "hold") return saveCopy("hold");
      if (state.kind === "permission") return saveCopy("permission");
      return plainSaveMessage(state.message);
    default:
      return "";
  }
}

function pageVisible() {
  return (
    typeof document === "undefined" || document.visibilityState !== "hidden"
  );
}

// ---------------------------------------------------------------------
// Redaction (defence in depth): the frozen-listing banner never shows an
// account / routing / tax-id style value in clear ("****" + last 4).
// - A key is sensitive when one of its tokens (split on _ - . and
//   camelCase) is a sensitive token — exact tokens, so `accounting_method`
//   or `reinvestment` are not — or it contains a sensitive phrase.
// - Sensitivity is inherited: everything nested under a sensitive key is
//   redacted.
// - A `{ label, value }` row whose label names such a number has its value
//   redacted.
// - Free text: any run of 9+ digits (spaces / dots / dashes allowed
//   between them) is masked, after HTML is stripped.
// ---------------------------------------------------------------------
const SENSITIVE_TOKENS = new Set([
  "account", "accounts", "acct", "routing", "aba", "ssn", "ein", "tin",
  "itin", "iban", "swift", "bic", "bank", "beneficiary", "escrow",
]);
const SENSITIVE_PHRASES = [
  /wire_?instructions?/i,
  /social_?security/i,
  /tax_?id/i,
  /invest_?now/i,
];
const SENSITIVE_LABEL =
  /\b(ein|ssn|tin|itin|tax\s*id|taxpayer|social\s*security|account|acct|routing|aba|iban|swift|bic)\b/i;
const LONG_DIGITS = /\d(?:[\s.-]?\d){8,}/g;

function keyTokens(key) {
  return String(key)
    .replace(/([a-z0-9])([A-Z])/g, "$1_$2")
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter(Boolean);
}

export function isSensitiveKey(key) {
  if (typeof key !== "string" || !key) return false;
  if (keyTokens(key).some((token) => SENSITIVE_TOKENS.has(token))) return true;
  return SENSITIVE_PHRASES.some((re) => re.test(key));
}

export function redactValue(value) {
  const text = String(value == null ? "" : value).replace(/<[^>]*>/g, "");
  const tail = text.replace(/[\s.-]+/g, "").slice(-4);
  return tail ? `****${tail}` : "****";
}

function maskLongDigits(text) {
  return text.replace(LONG_DIGITS, (run) => `****${run.replace(/\D/g, "").slice(-4)}`);
}

// Public SEC filing identifiers: shown as typed (never digit-masked) so the
// issuer can re-enter them after Reload.
const PUBLIC_IDENTIFIER_KEYS = new Set([
  "issuer_cik",
  "sec_file_number",
  "form_d_file_number",
]);

// LB-26 R2-4: links (the Form C / Form D EDGAR URLs, a video or webinar
// link) are shown as typed: an EDGAR path carries a CIK and an accession
// number ("…/data/1234567/000123456724000001/…") that the account-number
// mask would turn into "****0001", so the copied link no longer works. A
// key that names an account / routing / tax id is still redacted (the
// sensitive-key check runs first); free text keeps the digit mask.
const LINK_KEY = /(^|_)(url|urls|link|links|href|website)$/i;
const LINK_VALUE = /^\s*(https?:\/\/|www\.)\S+\s*$/i;

function isLinkValue(key, value) {
  if (typeof value !== "string") return false;
  const last = String(key == null ? "" : key).split(/[.\]]/).pop();
  return LINK_KEY.test(last) || LINK_VALUE.test(value);
}

// LB-26 R2-20: HTML entities from a rich-text editor ("&nbsp;", "&amp;")
// read as the characters they stand for in the dialog and copied text.
const NAMED_ENTITIES = {
  nbsp: " ",
  amp: "&",
  lt: "<",
  gt: ">",
  quot: '"',
  apos: "'",
  rsquo: "\u2019",
  lsquo: "\u2018",
  rdquo: "\u201d",
  ldquo: "\u201c",
  ndash: "\u2013",
  mdash: "\u2014",
  hellip: "\u2026",
  bull: "\u2022",
  middot: "\u00b7",
  copy: "\u00a9",
  reg: "\u00ae",
  trade: "\u2122",
};

export function decodeEntities(text) {
  return String(text == null ? "" : text).replace(
    /&(#x[0-9a-f]+|#\d+|[a-z]+);/gi,
    (match, body) => {
      if (body[0] === "#") {
        const code =
          body[1] === "x" || body[1] === "X"
            ? parseInt(body.slice(2), 16)
            : parseInt(body.slice(1), 10);
        if (!Number.isFinite(code) || code <= 0 || code > 0x10ffff) return match;
        const ch = String.fromCodePoint(code);
        return ch === "\u00a0" ? " " : ch;
      }
      const named = NAMED_ENTITIES[body.toLowerCase()];
      return named === undefined ? match : named;
    }
  );
}

// Tags removed, entities decoded, whitespace collapsed.
function visibleText(value) {
  return decodeEntities(String(value).replace(/<[^>]*>/g, " "))
    .replace(/\s+/g, " ")
    .trim();
}

function isCentsKey(key) {
  return typeof key === "string" && /_cents$/.test(key);
}

// A *_cents amount as dollars ($618,000.00).
function centsAsDollars(value) {
  const cents = Number(value);
  if (!Number.isFinite(cents)) return String(value);
  return `$${(cents / 100).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

// HCD-4: enum values shown by their plain-language label.
const SECURITY_COPY = (en && en.lb && en.lb.terms && en.lb.terms.security) || {};
function enumLabel(key, value) {
  if (typeof value !== "string") return null;
  const option = optionLabel(key, value);
  if (option) return option;
  if (key === "security_type") {
    const type = SECURITY_COPY.types && SECURITY_COPY.types[value];
    return (type && type.label) || null;
  }
  if (key === "pricing_method") {
    return (SECURITY_COPY.pricing && SECURITY_COPY.pricing.options && SECURITY_COPY.pricing.options[value]) || null;
  }
  if (key === "voting_rights") {
    return (SECURITY_COPY.voting && SECURITY_COPY.voting.options && SECURITY_COPY.voting.options[value]) || null;
  }
  return null;
}

// LB-26 R2-A: money terms read as dollars ("$750", "$2.50").
// LB-26 V1 F4: Determine Equity's amounts (debt, additional_debt,
// rental_details) are dollars too; a price always reads with cents ("$2.00").
const MONEY_KEY = /^(minimum_investment(_reg_cf|_rule_506c)?|share_price|goal|min_goal|max_goal|cash_goal|min_cash_goal|max_cash_goal|debt_cash_goal|valuation|valuation_cap|investment_multiple|qualified_financing_min|debt|additional_debt|rental_details)$/;
const PRICE_KEY = /^share_price$/;
function moneyText(value, key) {
  const last = String(key == null ? "" : key).split(/[.\]]/).pop();
  if (!MONEY_KEY.test(last)) return null;
  const n = typeof value === "number" ? value : /^-?\d+(\.\d+)?$/.test(String(value)) ? Number(value) : NaN;
  if (!Number.isFinite(n)) return null;
  const cents = PRICE_KEY.test(last) || Math.round(n * 100) % 100 !== 0;
  return `$${n.toLocaleString("en-US", {
    minimumFractionDigits: cents ? 2 : 0,
    maximumFractionDigits: 2,
  })}`;
}

// One non-sensitive scalar for the banner (`key` decides the format).
function displayScalar(value, key) {
  const label = enumLabel(key, value);
  if (label) return label;
  const money = moneyText(value, key);
  if (money) return money;
  if (isCentsKey(key) && (typeof value === "number" || /^\d+$/.test(String(value)))) {
    return centsAsDollars(value);
  }
  if (PUBLIC_IDENTIFIER_KEYS.has(key) || isLinkValue(key, value)) {
    return visibleText(value);
  }
  if (typeof value === "string") return plainText(value);
  return maskLongDigits(String(value));
}

function plainText(value) {
  return maskLongDigits(visibleText(value));
}

// A `{ label, value }` style row whose label names a sensitive number.
function sensitiveRow(obj) {
  if (!obj || typeof obj !== "object" || Array.isArray(obj)) return false;
  const label = obj.label || obj.name || obj.title || obj.key;
  return typeof label === "string" && SENSITIVE_LABEL.test(label);
}

const ROW_VALUE_KEYS = new Set(["value", "content", "text", "number"]);

// R2-7: bookkeeping keys a save carries that are not something the issuer
// typed (Propose Terms' `terms_answered` names fields whose values are
// listed on their own). Never shown as an unsaved / conflicting line.
export const UNSAVED_HIDDEN_KEYS = new Set(["terms_answered"]);

// The unsaved text of a section (its strings, never ids), shown by the
// frozen-listing banner so it can be copied before Reload discards it.
export function describeUnsaved(pending) {
  const parts = [];
  const visit = (value, key, inherited) => {
    if (value === null || value === undefined) return;
    const sensitive = inherited || isSensitiveKey(key);
    if (Array.isArray(value)) {
      value.forEach((item) => visit(item, key, sensitive));
    } else if (typeof value === "object") {
      const row = sensitiveRow(value);
      Object.keys(value).forEach((k) =>
        visit(value[k], k, sensitive || (row && ROW_VALUE_KEYS.has(k)))
      );
    } else if (sensitive) {
      parts.push(redactValue(value));
    } else if (typeof value === "string" || typeof value === "number") {
      const text = displayScalar(value, key);
      if (text) parts.push(text);
    }
  };
  Object.keys(pending || {})
    .filter((key) => !UNSAVED_HIDDEN_KEYS.has(key))
    .forEach((key) => visit(pending[key], key, false));
  return parts.join("\n");
}

const EMPTY_DISPLAY = "(empty)";
const CLEARED_DISPLAY = "(cleared)";

// Every leaf of a set of fields as `path -> { raw, display }`:
// null / undefined / "" -> "(empty)", booleans -> Yes / No, scalars as
// they are, empty objects / arrays -> "(empty)"; `display` is redacted as
// above, `raw` is the exact value (for comparing).
export function unsavedLeaves(fields) {
  const leaves = new Map();
  // LB-26 R2-A: an empty value reads "(empty)"; "(cleared)" is kept for a
  // value the user removed (formatChangedLeaves / changedAgainstBaseline).
  const scalar = (value, sensitive, key) => {
    if (value === null || value === undefined || value === "") return EMPTY_DISPLAY;
    if (sensitive) return redactValue(value);
    if (typeof value === "boolean") return value ? "Yes" : "No";
    return displayScalar(value, key) || EMPTY_DISPLAY;
  };
  const visit = (path, key, value, inherited) => {
    const sensitive = inherited || isSensitiveKey(key);
    if (Array.isArray(value)) {
      if (!value.length) {
        leaves.set(path, { raw: "[]", display: EMPTY_DISPLAY });
        return;
      }
      value.forEach((item, index) => visit(`${path}[${index + 1}]`, key, item, sensitive));
    } else if (value && typeof value === "object") {
      const keys = Object.keys(value);
      if (!keys.length) {
        leaves.set(path, { raw: "{}", display: EMPTY_DISPLAY });
        return;
      }
      const row = sensitiveRow(value);
      keys.forEach((k) =>
        visit(`${path}.${k}`, k, value[k], sensitive || (row && ROW_VALUE_KEYS.has(k)))
      );
    } else {
      leaves.set(path, {
        raw: JSON.stringify(value === undefined ? null : value),
        display: scalar(value, sensitive, key),
      });
    }
  };
  Object.keys(fields || {})
    .filter((key) => !UNSAVED_HIDDEN_KEYS.has(key))
    .forEach((key) => visit(key, key, fields[key], false));
  return leaves;
}

// `Field label: value` lines for a set of fields (every leaf), redacted.
// Labels come from listing-terms `fieldLabel` (never the raw key).
export function formatUnsavedFields(fields) {
  return Array.from(unsavedLeaves(fields).entries())
    .map(([path, leaf]) => `${fieldLabel(path)}: ${leaf.display}`)
    .join("\n");
}

// `field: value` lines for only the leaves that differ from `seedLeaves`
// (a removed leaf reads "(cleared)").
export function formatChangedLeaves(seedLeaves, fields) {
  const current = unsavedLeaves(fields);
  const lines = [];
  current.forEach((leaf, path) => {
    const before = seedLeaves && seedLeaves.get(path);
    if (!before || before.raw !== leaf.raw) {
      const display =
        leaf.display === EMPTY_DISPLAY && before && before.display !== EMPTY_DISPLAY
          ? CLEARED_DISPLAY
          : leaf.display;
      lines.push(`${fieldLabel(path)}: ${display}`);
    }
  });
  (seedLeaves || new Map()).forEach((leaf, path) => {
    if (!current.has(path) && leaf.raw !== "null") {
      lines.push(`${fieldLabel(path)}: (cleared)`);
    }
  });
  return lines.join("\n");
}

// The top-level keys whose leaves differ from `seedLeaves`.
export function changedTopKeys(seedLeaves, fields) {
  const keys = new Set();
  const top = (path) => String(path).split(/[.[]/)[0];
  const current = unsavedLeaves(fields);
  current.forEach((leaf, path) => {
    const before = seedLeaves && seedLeaves.get(path);
    if (!before || before.raw !== leaf.raw) keys.add(top(path));
  });
  (seedLeaves || new Map()).forEach((leaf, path) => {
    if (!current.has(path) && leaf.raw !== "null") keys.add(top(path));
  });
  return Array.from(keys);
}

// A whole-payload saver's unsaved values, only the leaves that differ
// from the baseline (an empty value where nothing was saved is not a
// change), so the dialog lists what the user changed, not the payload.
function changedAgainstBaseline(baseline, fields) {
  const base = {};
  Object.keys(fields || {}).forEach((key) => {
    if (Object.prototype.hasOwnProperty.call(baseline, key)) base[key] = baseline[key];
  });
  const before = unsavedLeaves(base);
  const lines = [];
  const keys = new Set();
  unsavedLeaves(fields).forEach((leaf, path) => {
    const prior = before.get(path);
    if (prior && prior.raw === leaf.raw) return;
    if (leaf.display === EMPTY_DISPLAY && (!prior || prior.display === EMPTY_DISPLAY)) return;
    const display = leaf.display === EMPTY_DISPLAY ? CLEARED_DISPLAY : leaf.display;
    lines.push(`${fieldLabel(path)}: ${display}`);
    keys.add(String(path).split(/[.[]/)[0]);
  });
  return { lines, keys: Array.from(keys) };
}

// Each section with unsaved edits for a listing: [{ section, text, keys }],
// formatted per key (`Field label: value`, booleans Yes/No, redacted).
// Unsaved = the request in flight ∪ pending (P1-1: a value the server is
// refusing right now is still the user's unsaved value).
export function unsavedSummary(homeId) {
  return allSavers()
    .filter(
      (saver) =>
        saver.summarize !== false &&
        String(saver.homeId) === String(homeId) &&
        Object.keys(saver.unsaved).length > 0
    )
    .map((saver) => {
      const fields = saver.unsaved;
      const changed = saver.isPrimed() ? changedAgainstBaseline(saver.baseline, fields) : null;
      if (changed && changed.lines.length) {
        return { section: saver.section, text: changed.lines.join("\n"), keys: changed.keys };
      }
      return {
        section: saver.section,
        text: formatUnsavedFields(fields),
        keys: Object.keys(fields),
      };
    })
    .filter((entry) => entry.text);
}

// Reload (D45): the unsaved edits of a listing are dropped from memory
// (the banner showed them first). Nothing is sent.
export function discardPendingForHome(homeId) {
  allSavers()
    .filter((saver) => String(saver.homeId) === String(homeId))
    .forEach((saver) => saver._discard());
  Array.from(orphanSavers)
    .filter((saver) => String(saver.homeId) === String(homeId))
    .forEach((saver) => orphanSavers.delete(saver));
  bumpUnsavedRevision();
}

// Everything the frozen-listing banner lists: pages first flush their
// buffered inputs (CKEditor), then every section saver (live or orphaned)
// and every whole-form editor's unsaved values.
export function collectUnsaved(homeId) {
  flushListingInputs(homeId);
  return [...unsavedSummary(homeId), ...unsavedFromSources(homeId)];
}

// LB-26 P1-1: "Now saved" — the server's current values (from the 409)
// for the keys the user has unsaved (all of them when no keys are known).
export function conflictSummary(homeId, keys) {
  const current = conflictCurrentFor(homeId);
  const wanted = Array.isArray(keys) && keys.length ? new Set(keys) : null;
  const fields = {};
  Object.keys(current).forEach((key) => {
    if (!wanted || wanted.has(key)) fields[key] = current[key];
  });
  return formatUnsavedFields(fields);
}

// LB-26 R2-A: once per "online" event, re-send every live section whose
// last save failed for a retryable reason (network / 5xx) and still holds
// edits. Frozen listings and refused (validation / locked) saves are left
// alone.
export function retryFailedSaves() {
  const savers = Array.from(liveSavers).filter((saver) => {
    const state = saver.state || {};
    return (
      state.status === SAVE_STATUS.ERROR &&
      state.kind === "retryable" &&
      saver.hasPending() &&
      !saver.inConflict()
    );
  });
  return Promise.all(savers.map((saver) => saver.retry()));
}

// Re-send, versioned, what a failed page-exit send left behind.
export function retryAfterVisible() {
  const savers = Array.from(retryWhenVisible);
  retryWhenVisible.clear();
  return Promise.all(savers.map((saver) => saver.flush()));
}

export function createSectionSaver({
  homeId,
  section,
  send,
  debounceMs = DEFAULT_DEBOUNCE_MS,
  retryDelays = DEFAULT_RETRY_DELAYS,
  onState,
  extra = {},
  sleep = defaultSleep,
  // A custom `send` that writes update_home (versioned) obeys the freeze;
  // the default endpoint always does.
  versioned,
  // false: the pending keys are triggers, not values (the page reports its
  // values through listingUnsavedSource instead).
  summarize = true,
} = {}) {
  const usesDefaultEndpoint = typeof send !== "function";
  const isVersioned = usesDefaultEndpoint || Boolean(versioned);
  const doSend = usesDefaultEndpoint
    ? (payload) => backend.update_home(payload, { caller: `listing-save.${section}` })
    : send;
  let pending = {};
  let keepaliveInFlight = false;
  let timer = null;
  let inFlight = null;
  // P1-1: the keys / values of the request on the wire (normal send or
  // page-exit keepalive) — still unsaved until it settles.
  let inFlightValues = null;
  // P1-2: what the server is known to hold, per key (primed by the page
  // from its own payload builder on hydrated data; a successful send
  // becomes the new baseline).
  let baseline = {};
  let disposed = false;
  // Set by the first replacePending(): the queue is the whole payload.
  let wholePayload = false;
  let state = { status: SAVE_STATUS.IDLE, section };

  function hasOwn(obj, key) {
    return Boolean(obj) && Object.prototype.hasOwnProperty.call(obj, key);
  }

  // The value an edit is compared with: the in-flight value while that key
  // is on the wire, else the baseline.
  function matchesReference(key, value) {
    if (hasOwn(inFlightValues, key)) return valuesEqual(inFlightValues[key], value);
    if (hasOwn(baseline, key)) return valuesEqual(baseline[key], value);
    return false;
  }

  function rebaseline(values) {
    Object.keys(values || {}).forEach((key) => {
      baseline[key] = cloneValue(values[key]);
    });
  }

  function cancelIfEmpty() {
    if (!Object.keys(pending).length) clearTimer();
  }

  function setState(next) {
    state = { section, ...next };
    if (typeof onState === "function") {
      try {
        onState(state);
      } catch (_) {
        // A rendering hook must never break the save queue.
      }
    }
  }

  function frozen() {
    return isVersioned && isHomeFrozen(homeId);
  }

  // R2-6 (P2-5): an archived listing opened in the issuer builder is
  // read-only; nothing is sent for it (edits made before the archive was
  // seen stay queued and listed, never dropped).
  function archived() {
    return isVersioned && isHomeArchived(homeId);
  }

  function showArchived() {
    clearTimer();
    if (state.status !== SAVE_STATUS.ERROR || state.code !== "listing_archived") {
      setState({
        status: SAVE_STATUS.ERROR,
        kind: "locked",
        code: "listing_archived",
        message: saveCopy("archived"),
      });
    }
  }

  function showFrozen() {
    if (state.status !== SAVE_STATUS.CONFLICT) {
      setState({ status: SAVE_STATUS.CONFLICT, frozen: true, message: FROZEN_STATE_MESSAGE });
    }
  }

  function clearTimer() {
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
  }

  function schedule() {
    clearTimer();
    if (frozen()) {
      showFrozen();
      return;
    }
    if (archived()) {
      showArchived();
      return;
    }
    timer = setTimeout(() => {
      timer = null;
      run();
    }, debounceMs);
  }

  function payloadFor(keys) {
    return { id: homeId, ...extra, ...keys };
  }

  async function attempt(keys, retryIndex) {
    if (frozen()) throw { name: "ListingFrozenError", frozen: true };
    try {
      return await doSend(payloadFor(keys));
    } catch (error) {
      const classified = classifySaveError(error);
      if (
        classified.status === SAVE_STATUS.ERROR &&
        classified.kind === "retryable" &&
        retryIndex < retryDelays.length
      ) {
        await sleep(retryDelays[retryIndex]);
        return attempt(keys, retryIndex + 1);
      }
      throw error;
    }
  }

  async function run() {
    // Wait out every send in flight (another run may start one while this
    // one waits), so two runs never send the same section at once.
    while (inFlight) {
      try {
        await inFlight;
      } catch (_) {
        // The previous send's outcome is already reflected in `state`.
      }
    }
    if (keepaliveInFlight) return state;
    const keys = pending;
    if (!Object.keys(keys).length) return state;
    if (frozen()) {
      showFrozen();
      return state;
    }
    if (archived()) {
      showArchived();
      return state;
    }
    pending = {};
    inFlightValues = keys;
    bumpUnsavedRevision();
    setState({ status: SAVE_STATUS.SAVING });
    const startedAt = Date.now();
    inFlight = attempt(keys, 0);
    // The whole attempt (retry sleeps included) counts as this tab's write
    // in progress: a hydration waits for it, a keepalive skips it.
    if (isVersioned && typeof holdHomeWrite === "function") {
      holdHomeWrite(homeId, inFlight.catch(() => {}));
    }
    try {
      const response = await inFlight;
      const data = (response && response.data) || {};
      const uncleared = Object.keys(keys).filter(
        (key) =>
          isEmptyValue(keys[key]) &&
          key in data &&
          !isEmptyValue(data[key]) &&
          data[key] !== undefined
      );
      // What was sent is now what the server holds (a key the server
      // would not empty keeps the server's value).
      rebaseline(keys);
      uncleared.forEach((key) => {
        baseline[key] = cloneValue(data[key]);
      });
      inFlightValues = null;
      bumpUnsavedRevision();
      setState({
        status: SAVE_STATUS.SAVED,
        savedAt: new Date().toISOString(),
        uncleared,
        response,
        // What this send carried (a page re-seeds from it).
        sent: keys,
      });
      trackSaveEvent("lb_save_succeeded", {
        home_id: homeId,
        section,
        ms: Date.now() - startedAt,
      });
    } catch (error) {
      // Never drop an edit: newer keystrokes win over the failed copy.
      // Restored (and the in-flight copy cleared) in the same tick, before
      // the freeze is observed below, so the dialog always lists them.
      pending = { ...keys, ...pending };
      inFlightValues = null;
      bumpUnsavedRevision();
      const classified = classifySaveError(error);
      if (classified.status === SAVE_STATUS.CONFLICT) {
        // The backend froze the listing (409 / 428); make sure of it for a
        // custom send that bypassed backend.update_home.
        if (isVersioned) {
          if (error && error.current) recordConflictCurrent(homeId, error.current);
          freezeHome(homeId, "conflict");
        }
        trackSaveEvent("lb_conflict_shown", { home_id: homeId, section });
      } else {
        trackSaveEvent("lb_save_failed", {
          home_id: homeId,
          section,
          kind: classified.kind,
        });
      }
      setState({ ...classified, error });
    } finally {
      inFlight = null;
      inFlightValues = null;
    }
    if (
      !disposed &&
      state.status === SAVE_STATUS.SAVED &&
      Object.keys(pending).length
    ) {
      schedule();
    }
    return state;
  }

  function markPending() {
    // An edit after "Saved ✓" is unsaved again.
    if (state.status === SAVE_STATUS.SAVED) setState({ status: SAVE_STATUS.IDLE });
  }

  const saver = {
    section,
    homeId,
    versioned: isVersioned,
    summarize,
    get state() {
      return state;
    },
    get pending() {
      return { ...pending };
    },
    // P1-1: everything not yet saved — the request in flight and pending
    // edits (pending wins: it is newer).
    get unsaved() {
      return { ...(inFlightValues || {}), ...pending };
    },
    get inFlightValues() {
      return inFlightValues ? { ...inFlightValues } : null;
    },
    get baseline() {
      return { ...baseline };
    },
    isPrimed(key) {
      return key === undefined ? Object.keys(baseline).length > 0 : hasOwn(baseline, key);
    },
    // P1-2: the server's values for these keys (the page's payload builder
    // run on freshly hydrated data, after a Reload, after a CKEditor
    // @ready). A queued edit that now equals its baseline is dropped (for
    // a whole-payload saver: only when the whole queue equals it).
    prime(values) {
      rebaseline(values);
      if (Object.keys(pending).length) {
        if (wholePayload) {
          const same = Object.keys(pending).every((key) =>
            matchesReference(key, pending[key])
          );
          if (same) pending = {};
        } else {
          Object.keys(values || {}).forEach((key) => {
            if (hasOwn(pending, key) && matchesReference(key, pending[key])) {
              delete pending[key];
            }
          });
        }
        cancelIfEmpty();
      }
      bumpUnsavedRevision();
      return saver;
    },
    // True when `values` differs from what the server holds (baseline /
    // in flight) for some key; a key with no baseline counts as changed.
    differs(values) {
      return Object.keys(values || {}).some((key) => !matchesReference(key, values[key]));
    },
    usesDefaultEndpoint,
    hasPending() {
      return Object.keys(pending).length > 0 || Boolean(inFlight);
    },
    // True while this section cannot save because its listing is frozen.
    inConflict() {
      return state.status === SAVE_STATUS.CONFLICT || frozen();
    },
    isInFlight() {
      return Boolean(inFlight) || keepaliveInFlight;
    },
    // Per-key: a value equal to the baseline (or to the value in flight)
    // removes the key, so A→B→A within the debounce sends nothing.
    set(key, value) {
      return saver.setMany({ [key]: value });
    },
    setMany(values) {
      let queued = false;
      Object.keys(values || {}).forEach((key) => {
        if (matchesReference(key, values[key])) {
          delete pending[key];
        } else {
          // R2-2: a snapshot, never the page's live object.
          pending[key] = snapshotValue(values[key]);
          queued = true;
        }
      });
      bumpUnsavedRevision();
      if (queued) {
        markPending();
        schedule();
      } else {
        cancelIfEmpty();
      }
      return saver;
    },
    // Whole-payload editors (Propose Terms, admin autosave): the latest
    // payload replaces whatever was queued, so a key the editor stopped
    // sending is dropped. P1-2 gate: the full payload is queued only when
    // some key differs from the baseline; keys are never stripped (the
    // server validates terms together).
    replacePending(values) {
      wholePayload = true;
      // R2-2: a snapshot, never the page's live arrays.
      const next = snapshotValue(values || {});
      bumpUnsavedRevision();
      if (!Object.keys(next).length || !saver.differs(next)) {
        // Nothing to send (an empty replacement is not an edit: no
        // Saved -> idle flicker).
        pending = {};
        clearTimer();
        return saver;
      }
      markPending();
      pending = next;
      schedule();
      return saver;
    },
    // Resolves when nothing is pending or in flight — or the section
    // stopped on an error / its listing is frozen (pending stays queued).
    async flush() {
      for (;;) {
        clearTimer();
        await run();
        if (inFlight) continue;
        if (!Object.keys(pending).length) return state;
        if (
          disposed ||
          keepaliveInFlight ||
          frozen() ||
          state.status === SAVE_STATUS.ERROR ||
          state.status === SAVE_STATUS.CONFLICT
        ) {
          if (frozen()) showFrozen();
          return state;
        }
      }
    },
    retry() {
      if (frozen()) return Promise.resolve(state);
      return saver.flush();
    },
    // Reload (D45): forget the unsaved edits; the page rehydrates.
    _discard() {
      clearTimer();
      pending = {};
      retryWhenVisible.delete(saver);
      bumpUnsavedRevision();
      setState({ status: SAVE_STATUS.IDLE });
    },
    // LB-18b M3: the page-exit path. `keepaliveFlushAll` merges every
    // default-endpoint saver of one listing into ONE keepalive request;
    // these hooks let it take a saver's pending keys and settle them.
    canKeepalive() {
      return (
        usesDefaultEndpoint &&
        Object.keys(pending).length > 0 &&
        !inFlight &&
        !keepaliveInFlight &&
        !frozen() &&
        !archived()
      );
    },
    extra,
    _keepaliveTake() {
      clearTimer();
      keepaliveInFlight = true;
      return { ...pending };
    },

    // Drop only what was sent and not edited since. A 409 / 428 freezes
    // the listing; any other failure keeps everything as error(retryable),
    // re-sent when the page is visible again. Never "Saved ✓" with edits
    // pending.
    _keepaliveSettle(sent, outcome) {
      keepaliveInFlight = false;
      bumpUnsavedRevision();
      const result =
        typeof outcome === "object" && outcome !== null ? outcome : { ok: Boolean(outcome) };
      if (!result.ok) {
        const data = result.data || {};
        if (
          (result.status === 409 && data.code === "version_conflict") ||
          result.status === 428 ||
          data.code === "version_required"
        ) {
          freezeHome(homeId, result.status === 428 ? "version_required" : "conflict");
          showFrozen();
          return;
        }
        setState({ status: SAVE_STATUS.ERROR, kind: "retryable", message: "Couldn't save" });
        if (!disposed) {
          if (pageVisible()) schedule();
          else retryWhenVisible.add(saver);
        }
        return;
      }
      rebaseline(sent);
      Object.keys(sent).forEach((key) => {
        if (key in pending && valuesEqual(pending[key], sent[key])) {
          delete pending[key];
        }
      });
      bumpUnsavedRevision();
      if (!Object.keys(pending).length) {
        setState({
          status: SAVE_STATUS.SAVED,
          savedAt: new Date().toISOString(),
          sent,
          // The server's payload, so a page can adopt new ids from it.
          response: { data: result.data },
        });
      } else if (!disposed) {
        // What was sent is saved (a page adopts ids from it) — reported
        // and replaced in the same tick, so "Saved" never shows while
        // edits made during the keepalive still need saving.
        setState({
          status: SAVE_STATUS.SAVED,
          savedAt: new Date().toISOString(),
          sent,
          response: { data: result.data },
        });
        setState({ status: SAVE_STATUS.IDLE });
        if (pageVisible()) schedule();
        else retryWhenVisible.add(saver);
      }
    },
    dispose() {
      disposed = true;
      clearTimer();
      liveSavers.delete(saver);
      retryWhenVisible.delete(saver);
      if (Object.keys(pending).length && isVersioned) orphanSavers.add(saver);
    },
  };
  liveSavers.add(saver);
  return saver;
}

// Map section name -> keys, plus a snapshot diff: `update(fullPayload)`
// enqueues, per section, only the keys whose value changed since the last
// enqueue. Lets a step that rebuilds its whole payload on every edit still
// send only the sections that actually changed.
export function createSectionGroup({ homeId, sections, onState, ...options }) {
  const savers = {};
  const snapshots = {};
  Object.keys(sections).forEach((name) => {
    savers[name] = createSectionSaver({
      homeId,
      section: name,
      onState: (state) => onState && onState(name, state),
      ...options,
    });
  });
  return {
    savers,
    // P1-2: the page's payload builder on freshly hydrated data (and after
    // Reload / a CKEditor @ready): the diff snapshot AND each saver's
    // baseline. `onlyKeys` primes just those keys (an editor that became
    // ready while other fields may already hold edits).
    prime(fullPayload, onlyKeys) {
      const only = Array.isArray(onlyKeys) ? new Set(onlyKeys) : null;
      Object.keys(sections).forEach((name) => {
        const values = {};
        sections[name].forEach((key) => {
          if (!(key in fullPayload)) return;
          if (only && !only.has(key)) return;
          snapshots[key] = cloneValue(fullPayload[key]);
          values[key] = fullPayload[key];
        });
        if (Object.keys(values).length) savers[name].prime(values);
      });
    },
    update(fullPayload) {
      Object.keys(sections).forEach((name) => {
        const changed = {};
        sections[name].forEach((key) => {
          if (!(key in fullPayload)) return;
          const next = fullPayload[key];
          if (!(key in snapshots) || !valuesEqual(snapshots[key], next)) {
            snapshots[key] = cloneValue(next);
            changed[key] = next;
          }
        });
        if (Object.keys(changed).length) savers[name].setMany(changed);
      });
    },
    flush() {
      return Promise.all(Object.values(savers).map((s) => s.flush()));
    },
    hasPending() {
      return Object.values(savers).some((s) => s.hasPending());
    },
    conflicted() {
      return Object.values(savers).filter((s) => s.inConflict());
    },
    dispose() {
      Object.values(savers).forEach((s) => s.dispose());
    },
  };
}

export function flushAll() {
  return Promise.all(Array.from(liveSavers).map((saver) => saver.flush()));
}

// `exceptHomeId`: ignore that listing's edits (the banner's deliberate
// full-page Reload discards exactly those).
export function hasUnsavedEdits(exceptHomeId) {
  return (
    allSavers().some(
      (saver) =>
        saver.hasPending() &&
        (exceptHomeId == null || String(saver.homeId) !== String(exceptHomeId))
    ) || anyUnsavedFromSources(exceptHomeId)
  );
}

// Next/submit must not proceed while any section's listing is frozen.
export function conflictedSavers(savers) {
  return (savers || Array.from(liveSavers)).filter(
    (saver) => saver && saver.inConflict()
  );
}

// keepalive requests are capped by browsers (64 KB across in-flight
// keepalive bodies). A bigger payload is not sent; the beforeunload prompt
// stays up instead so the issuer can stay and let the normal save run.
export const MAX_KEEPALIVE_BYTES = 64 * 1024;

function keepaliveHeaders() {
  const common =
    (axios.defaults && axios.defaults.headers && axios.defaults.headers.common) ||
    {};
  const headers = { "Content-Type": "application/json" };
  Object.keys(common).forEach((name) => {
    if (typeof common[name] === "string") headers[name] = common[name];
  });
  headers["X-Requested-With"] = headers["X-Requested-With"] || "XMLHttpRequest";
  headers["X-LB-Caller"] = "listing-save.keepalive";
  return headers;
}

// LB-18b M3: one merged keepalive payload per listing, sent from
// visibilitychange→hidden (the last reliable moment on mobile) and again
// from pagehide only for what is still unsent. Never for a frozen listing
// (its edits stay in memory and the leave prompt stays up). Non-listing
// savers (custom send) just flush. Returns the number of keepalive
// requests sent.
export function keepaliveFlushAll(fetchImpl) {
  const doFetch = fetchImpl || (typeof fetch === "function" ? fetch : null);
  const byHome = new Map();
  Array.from(liveSavers).forEach((saver) => {
    if (!saver.usesDefaultEndpoint) {
      if (saver.hasPending()) saver.flush();
      return;
    }
    if (!saver.canKeepalive()) return;
    // Hydrated before, yet no version now (a Reload in progress or lost):
    // never send blind — freeze instead.
    if (
      typeof wasHydrated === "function" &&
      wasHydrated(saver.homeId) &&
      typeof getHomeVersion === "function" &&
      getHomeVersion(saver.homeId) === undefined
    ) {
      freezeHome(saver.homeId, "version_lost");
      return;
    }
    // A write for this listing is still queued / running in this tab: a
    // keepalive now would race it.
    if (typeof hasPendingHomeWrite === "function" && hasPendingHomeWrite(saver.homeId)) {
      return;
    }
    const key = String(saver.homeId);
    if (!byHome.has(key)) byHome.set(key, []);
    byHome.get(key).push(saver);
  });
  if (!doFetch) return 0;
  let sentCount = 0;
  byHome.forEach((savers) => {
    const homeId = savers[0].homeId;
    const preview = { id: homeId };
    savers.forEach((saver) => Object.assign(preview, saver.extra, saver.pending));
    const version =
      typeof getHomeVersion === "function" ? getHomeVersion(homeId) : undefined;
    if (version !== undefined) preview.expected_version = version;
    const body = JSON.stringify(preview);
    if (body.length > MAX_KEEPALIVE_BYTES) return;
    const taken = savers.map((saver) => [saver, saver._keepaliveTake()]);
    const settle = (outcome) =>
      taken.forEach(([saver, sent]) => saver._keepaliveSettle(sent, outcome));
    sentCount += 1;
    try {
      const request = Promise.resolve(
        doFetch(UPDATE_HOME_URL, {
          method: "POST",
          keepalive: true,
          credentials: "include",
          headers: keepaliveHeaders(),
          body,
        })
      );
      // Later writes for this listing wait for the keepalive.
      if (typeof trackHomeWrite === "function") {
        trackHomeWrite(homeId, request).catch(() => {});
      }
      request.then(
        async (res) => {
          const ok = Boolean(res && res.ok);
          let data = null;
          try {
            data = res && typeof res.json === "function" ? await res.json() : null;
          } catch (_) {
            data = null;
          }
          // Saved: this tab's own write.
          if (ok && data && data.edit_version != null && typeof setHomeVersion === "function") {
            setHomeVersion(homeId, data.edit_version, { own: true });
          }
          settle({ ok, status: res && res.status, data });
          trackSaveEvent(ok ? "lb_save_succeeded" : "lb_save_failed", {
            home_id: homeId,
            section: "keepalive",
            ...(ok ? { ms: 0 } : { kind: "keepalive" }),
          });
        },
        () => settle({ ok: false })
      );
    } catch (_) {
      settle({ ok: false });
    }
  });
  return sentCount;
}

let lifecycleInstalled = false;
// Set right before the banner's deliberate full-page Reload: that
// listing's edits were shown and are discarded on purpose; any OTHER
// listing's unsaved edits still prompt.
let leavePromptSuppressedFor = null;

export function suppressLeavePrompt(homeId) {
  leavePromptSuppressedFor = homeId == null ? null : String(homeId);
}

export function installPageLifecycle(win = typeof window !== "undefined" ? window : null) {
  if (!win || lifecycleInstalled) return () => {};
  const doc = win.document;
  const onVisibility = () => {
    if (!doc) return;
    if (doc.visibilityState === "hidden") keepaliveFlushAll();
    else retryAfterVisible();
  };
  const onPageHide = () => keepaliveFlushAll();
  // LB-26 R2-A: back online -> one automatic retry of the saves that
  // failed for lack of a connection.
  const onOnline = () => retryFailedSaves();
  const onBeforeUnload = (event) => {
    if (!hasUnsavedEdits(leavePromptSuppressedFor)) return undefined;
    event.preventDefault();
    event.returnValue = "";
    return "";
  };
  if (doc) doc.addEventListener("visibilitychange", onVisibility);
  win.addEventListener("pagehide", onPageHide);
  win.addEventListener("beforeunload", onBeforeUnload);
  win.addEventListener("online", onOnline);
  lifecycleInstalled = true;
  return () => {
    if (doc) doc.removeEventListener("visibilitychange", onVisibility);
    win.removeEventListener("pagehide", onPageHide);
    win.removeEventListener("beforeunload", onBeforeUnload);
    win.removeEventListener("online", onOnline);
    lifecycleInstalled = false;
  };
}

export function _resetForTests() {
  Array.from(liveSavers).forEach((saver) => saver.dispose());
  liveSavers.clear();
  orphanSavers.clear();
  retryWhenVisible.clear();
  leavePromptSuppressedFor = null;
}
